import { Button, Form, Pagination, Table } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useDispatch, useSelector } from "react-redux"
import { camelCaseToWords, formatDate, getApplicationStatusSelectValue } from "../../../constant/utils"
import Loader from "../../../components/Loader"
import { css } from "@emotion/react"
import { useEffect, useState } from "react"
import { changeApplicationStatus, getDashboardList } from "../../../data/applicationSlice"
import { getApplicationStatus } from "../../../data/enumSlice"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"
import { useNavigate } from "react-router-dom"

const componentStyle = css`
    p.note {
        color: #333;
        background: gold;
        font-size: 14px;
        margin: 12px 0 0 0;
    }
`

const ApplicationsOnBehalfOfAgencies = ({ handleInformationShow, handleOpenModal, handleComments }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { dashboardData, fetchStatus, dashboardDataRecords } = useSelector((state) => state.application)
    const { applicationStatus } = useSelector((state) => state.enums)
    const [activePage, setActivePage] = useState(0)
    const [isChangingPage, setIsChangingPage] = useState(false)

    const numberOfRecordsPerPage = 25
    const totalPages = dashboardDataRecords > 0 ? Math.ceil(dashboardDataRecords / numberOfRecordsPerPage) : 0

    useEffect(() => {
        !applicationStatus?.length && dispatch(getApplicationStatus())
    }, [])

    useEffect(() => {
        setActivePage(0)
        getDashboardListData()
        // eslint-disable-next-line
    }, [])

    const handlePageChange = async (index) => {
        if (index >= 0 && index < totalPages && index !== activePage && !isChangingPage && totalPages > 0) {
            setIsChangingPage(true)
            setActivePage(index)
            try {
                await getDashboardListData(index + 1)
            } catch (error) {
                console.error("Error changing page:", error)
                // Revert to previous page on error
                setActivePage(activePage)
            } finally {
                setIsChangingPage(false)
            }
        }
    }

    const getDashboardListData = (pageIndex = 1) => {
        if (pageIndex < 1) pageIndex = 1
        return dispatch(
            getDashboardList({
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstNamedInsured",
                descending: true
            })
        )
    }

    const changeStatus = (id, status) => {
        if (!status) return
        dispatch(changeApplicationStatus({ id, status })).then(({ payload }) => {
            if (payload?.succeeded) {
                getDashboardListData(activePage + 1)
            }
        })
    }

    const renderPagination = () => {
        const paginationItems = []
        const ellipsis = (
            <Pagination.Ellipsis
                key="ellipsis"
                disabled
            />
        )
        const pagesToShow = 5
        const tailPagesToShow = 2

        paginationItems.push(
            <Pagination.First
                key="first"
                onClick={() => handlePageChange(0)}
                disabled={activePage === 0}
            />
        )
        paginationItems.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 0}
            />
        )

        if (totalPages <= pagesToShow + tailPagesToShow) {
            for (let i = 0; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        } else {
            paginationItems.push(
                <Pagination.Item
                    key={0}
                    onClick={() => handlePageChange(0)}
                    active={activePage === 0}>
                    {1}
                </Pagination.Item>
            )
            const startPage = Math.max(1, activePage - Math.floor((pagesToShow - 1) / 2))
            const endPage = Math.min(totalPages - tailPagesToShow, startPage + pagesToShow - 1)
            if (startPage > 1) paginationItems.push(ellipsis)
            for (let i = startPage; i < endPage; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
            if (endPage < totalPages - tailPagesToShow) paginationItems.push(ellipsis)
            for (let i = totalPages - tailPagesToShow; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        }
        paginationItems.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        paginationItems.push(
            <Pagination.Last
                key="last"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        return paginationItems
    }

    const handleEdit = (id) => {
        navigate(`/admin/create-application`, { 
            state: { 
                applicationId: id,
                isEdit: true,
                isOnBehalf: true,
                adminEdit: true
            } 
        })
    }

    return (
        <div css={componentStyle}>
            <Loader show={fetchStatus === "loading" || isChangingPage} />
            <h2>Applications on Behalf of Agencies</h2>
            {dashboardData?.length === 0 && fetchStatus === "success" && (
                <p className="mt-4 pt-3 text-center h5">No Results</p>
            )}
            {(() => {
                return dashboardData?.length > 0
            })() && (
                <>
                    <Table
                        striped
                        bordered
                        hover
                        responsive
                        className="centered-row mt-4">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>#</th>
                                <th>First Named Insured</th>
                                <th>Agency Name</th>
                                <th>Agency Contact</th>
                                <th>Date Started</th>
                                <th>Date Submitted</th>
                                <th>Status</th>
                                <th>Application</th>
                                <th>Change Status</th>
                                <th>Edit</th>
                                <th>View Comments</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dashboardData?.map((application, index) => (
                                <tr key={index}>
                                    <td>{application?.friendlyId}</td>
                                    <td>{application?.customerNumber || ""}</td>
                                    <td>{application?.firstNamedInsured}</td>
                                    <td className="cursor-pointer">
                                        {application?.agencyName}
                                        {application?.agencyName && (
                                            <FontAwesomeIcon
                                                onClick={() => handleInformationShow("Agency", application?.agencyId)}
                                                className="mx-2 cursor-pointer"
                                                icon={byPrefixAndName.fal["circle-info"]}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {application?.agencyContact}
                                        {application?.agencyContact && (
                                            <FontAwesomeIcon
                                                onClick={() =>
                                                    handleInformationShow("Contact", application?.agencyContactId)
                                                }
                                                className="cursor-pointer mx-2"
                                                icon={byPrefixAndName.fal["circle-info"]}
                                            />
                                        )}
                                    </td>
                                    <td>{formatDate(application?.dateStarted)}</td>
                                    <td>{formatDate(application?.dateSubmitted)}</td>
                                    <td>{camelCaseToWords(application?.status)}</td>
                                    <td style={{ textAlign: "center" }}>
                                        <Button
                                            onClick={() => handleOpenModal(application?.id)}
                                            variant="light"
                                            className="me-2 btn-sm">
                                            <FontAwesomeIcon icon={byPrefixAndName.fal["magnifying-glass"]} />
                                        </Button>
                                    </td>
                                    <td>
                                        <Form.Select
                                            name="status"
                                            className="form-select-sm"
                                            onChange={(e) => changeStatus(application?.id, e.target.value)}
                                            value={getApplicationStatusSelectValue(
                                                applicationStatus,
                                                application?.status
                                            )}>
                                            <option value={""}>--- Select ---</option>
                                            {applicationStatus?.map((status, i) => (
                                                <option
                                                    key={i}
                                                    value={status?.name}>
                                                    {status?.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        <Button
                                            onClick={() => handleEdit(application?.id)}
                                            variant="primary"
                                            className="me-2 btn-sm">
                                            <FontAwesomeIcon icon={byPrefixAndName.fal["pen-to-square"]} />
                                        </Button>
                                    </td>
                                    <td
                                        onClick={() => handleComments(application?.id)}
                                        className="text-primary h6"
                                        role="button">
                                        Comments
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {/* Debug: Always show pagination for testing */}
                    {totalPages > 0 && (
                        <div className="mt-4">
                            <Pagination
                                className="d-flex justify-content-end"
                                size="md">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default ApplicationsOnBehalfOfAgencies
